import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Trash2, Calendar, Download, MessageSquare, Edit3, MessageCircle, Check, XCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import fileService from '../services/fileService';
import contextService from '../services/contextService';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled(motion.div)`
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
`;

const TabBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const TabButton = styled.button`
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  background: ${props => props.$active ? (props.theme.mode === 'dark' ? '#0969da' : '#0969da') : (props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa')};
  color: ${props => props.$active ? '#ffffff' : (props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e')};
  cursor: pointer;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#586069'};
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
`;

const FileList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.mode === 'dark' ? '#2d333b' : '#f0f0f0'};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.mode === 'dark' ? '#484f58' : '#c1c1c1'};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.mode === 'dark' ? '#6e7681' : '#a8b2ba'};
  }
`;

const FileItem = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
  
  &:hover {
    background: ${props => props.theme.mode === 'dark' ? '#30363d' : '#e1e4e8'};
    border-color: ${props => props.theme.mode === 'dark' ? '#484f58' : '#d1d9e0'};
  }
`;

const FileIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.theme.mode === 'dark' ? '#0969da' : '#0969da'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  color: white;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: ${props => props.$editable ? 'text' : 'default'};
  user-select: ${props => props.$editable ? 'text' : 'none'};
`;

const FileNameInput = styled.input`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  background: ${props => props.theme.mode === 'dark' ? '#2d333b' : '#ffffff'};
  border: 2px solid ${props => props.theme.mode === 'dark' ? '#0969da' : '#0969da'};
  border-radius: 4px;
  padding: 4px 8px;
  width: 100%;
  outline: none;
  margin-bottom: 4px;
`;

const FileMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#586069'};
`;

const FileActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#586069'};
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#586069'};
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#586069'};
`;

const EmptyText = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const EmptySubtext = styled.div`
  font-size: 14px;
  opacity: 0.8;
`;

function RecentFiles({ isOpen, onClose, onLoadFile, onSelectSession }) {
  const { theme } = useTheme();
  const [recentFiles, setRecentFiles] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeTab, setActiveTab] = useState('files'); // 'files' | 'sessions'
  const [editingFileName, setEditingFileName] = useState(null); // 편집 중인 파일 이름
  const [editingSessionId, setEditingSessionId] = useState(null); // 편집 중인 세션 ID
  const [editValue, setEditValue] = useState(''); // 편집 중인 값

  useEffect(() => {
    if (isOpen) {
      const files = fileService.getRecentFiles();
      setRecentFiles(files);
      setSessions(contextService.getSessions());
    } else {
      // 모달이 닫힐 때 편집 상태 초기화
      setEditingFileName(null);
      setEditingSessionId(null);
      setEditValue('');
    }
  }, [isOpen]);

  const handleLoadFile = (file) => {
    onLoadFile(file.content);
    onClose();
  };

  const handleDeleteFile = (fileName, e) => {
    e.stopPropagation();
    fileService.deleteRecentFile(fileName);
    setRecentFiles(prev => prev.filter(file => file.name !== fileName));
  };

  const handleDownloadFile = (file, e) => {
    e.stopPropagation();
    if (file.type === 'word') {
      fileService.saveAsWord(file.content, file.name);
    } else {
      fileService.saveAsText(file.content, file.name);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'word':
        return '📄';
      case 'text':
        return '📝';
      default:
        return '📄';
    }
  };

  const handleLoadSession = (sessionId, mode) => {
    if (onSelectSession) {
      onSelectSession(sessionId, mode);
    } else {
      contextService.setCurrentSession(sessionId);
    }
    onClose();
  };

  // 파일 이름 편집 시작
  const handleStartEditFile = (fileName, e) => {
    e.stopPropagation();
    setEditingFileName(fileName);
    setEditValue(fileName);
  };

  // 파일 이름 편집 취소
  const handleCancelEditFile = () => {
    setEditingFileName(null);
    setEditValue('');
  };

  // 파일 이름 편집 완료
  const handleSaveEditFile = (oldName) => {
    if (editValue.trim() && editValue.trim() !== oldName) {
      if (fileService.renameRecentFile(oldName, editValue.trim())) {
        setRecentFiles(fileService.getRecentFiles());
      }
    }
    setEditingFileName(null);
    setEditValue('');
  };

  // 세션 이름 편집 시작
  const handleStartEditSession = (sessionId, currentTitle, e) => {
    e.stopPropagation();
    setEditingSessionId(sessionId);
    setEditValue(currentTitle || '대화');
  };

  // 세션 이름 편집 취소
  const handleCancelEditSession = () => {
    setEditingSessionId(null);
    setEditValue('');
  };

  // 세션 이름 편집 완료
  const handleSaveEditSession = (sessionId) => {
    if (editValue.trim()) {
      contextService.updateSessionTitle(sessionId, editValue.trim());
      setSessions(contextService.getSessions());
    }
    setEditingSessionId(null);
    setEditValue('');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <Modal
            theme={theme}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Header theme={theme}>
              <Title theme={theme}>최근</Title>
              <CloseButton theme={theme} onClick={onClose}>
                <X size={20} />
              </CloseButton>
            </Header>

            <div style={{ paddingBottom: 8 }}>
              <TabBar>
                <TabButton theme={theme} $active={activeTab === 'files'} onClick={() => setActiveTab('files')}>
                  파일 ({recentFiles.length})
                </TabButton>
                <TabButton theme={theme} $active={activeTab === 'sessions'} onClick={() => setActiveTab('sessions')}>
                  대화 ({sessions.length})
                </TabButton>
              </TabBar>
            </div>

            {recentFiles.length === 0 && sessions.length === 0 ? (
              <EmptyState theme={theme}>
                <EmptyIcon theme={theme}>
                  <FileText size={32} />
                </EmptyIcon>
                <EmptyText theme={theme}>최근 항목이 없습니다</EmptyText>
                <EmptySubtext theme={theme}>
                  문서를 저장하거나 대화를 시작하면 여기에 표시됩니다
                </EmptySubtext>
              </EmptyState>
            ) : (
              <div>
                {activeTab === 'files' ? (
                  <FileList theme={theme}>
                    {recentFiles.map((file, index) => (
                  <FileItem
                    key={file.name}
                    theme={theme}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleLoadFile(file)}
                  >
                    <FileIcon theme={theme}>
                      {getFileTypeIcon(file.type)}
                    </FileIcon>
                    <FileInfo>
                      {editingFileName === file.name ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                          <FileNameInput
                            theme={theme}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveEditFile(file.name);
                              } else if (e.key === 'Escape') {
                                handleCancelEditFile();
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                          <ActionButton
                            theme={theme}
                            onClick={(e) => { e.stopPropagation(); handleSaveEditFile(file.name); }}
                            title="저장"
                          >
                            <Check size={14} />
                          </ActionButton>
                          <ActionButton
                            theme={theme}
                            onClick={(e) => { e.stopPropagation(); handleCancelEditFile(); }}
                            title="취소"
                          >
                            <XCircle size={14} />
                          </ActionButton>
                        </div>
                      ) : (
                        <FileName 
                          theme={theme}
                          $editable={false}
                          onDoubleClick={(e) => handleStartEditFile(file.name, e)}
                        >
                          {file.name}
                        </FileName>
                      )}
                      <FileMeta theme={theme}>
                        <span>
                          <Calendar size={12} style={{ marginRight: 4 }} />
                          {formatDate(file.savedAt)}
                        </span>
                        <span style={{ textTransform: 'uppercase' }}>
                          {file.type}
                        </span>
                      </FileMeta>
                    </FileInfo>
                    <FileActions>
                      <ActionButton
                        theme={theme}
                        onClick={(e) => handleStartEditFile(file.name, e)}
                        title="이름 변경"
                      >
                        <Edit3 size={16} />
                      </ActionButton>
                      <ActionButton
                        theme={theme}
                        onClick={(e) => handleDownloadFile(file, e)}
                        title="다운로드"
                      >
                        <Download size={16} />
                      </ActionButton>
                      <ActionButton
                        theme={theme}
                        onClick={(e) => handleDeleteFile(file.name, e)}
                        title="삭제"
                      >
                        <Trash2 size={16} />
                      </ActionButton>
                    </FileActions>
                  </FileItem>
                    ))}
                  </FileList>
                ) : (
                  <FileList theme={theme}>
                    {sessions.map((s, index) => (
                      <FileItem
                        key={s.id}
                        theme={theme}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleLoadSession(s.id)}
                      >
                        <FileIcon theme={theme}>
                          <MessageSquare size={18} />
                        </FileIcon>
                        <FileInfo>
                          {editingSessionId === s.id ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                              <FileNameInput
                                theme={theme}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveEditSession(s.id);
                                  } else if (e.key === 'Escape') {
                                    handleCancelEditSession();
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                              <ActionButton
                                theme={theme}
                                onClick={(e) => { e.stopPropagation(); handleSaveEditSession(s.id); }}
                                title="저장"
                              >
                                <Check size={14} />
                              </ActionButton>
                              <ActionButton
                                theme={theme}
                                onClick={(e) => { e.stopPropagation(); handleCancelEditSession(); }}
                                title="취소"
                              >
                                <XCircle size={14} />
                              </ActionButton>
                            </div>
                          ) : (
                            <FileName 
                              theme={theme}
                              $editable={false}
                              onDoubleClick={(e) => handleStartEditSession(s.id, s.title, e)}
                            >
                              {s.title || '대화'}
                            </FileName>
                          )}
                          <FileMeta theme={theme}>
                            <span>
                              <Calendar size={12} style={{ marginRight: 4 }} />
                              {formatDate(s.updatedAt || s.createdAt)}
                            </span>
                            <span>
                              메시지 {s.messages?.length || 0}개
                            </span>
                          </FileMeta>
                        </FileInfo>
                        <FileActions>
                          <ActionButton
                            theme={theme}
                            onClick={(e) => handleStartEditSession(s.id, s.title, e)}
                            title="이름 변경"
                          >
                            <Edit3 size={16} />
                          </ActionButton>
                          <ActionButton
                            theme={theme}
                            onClick={(e) => { e.stopPropagation(); contextService.deleteSession(s.id); setSessions(contextService.getSessions()); }}
                            title="대화 삭제"
                          >
                            <Trash2 size={16} />
                          </ActionButton>
                          <ActionButton
                            theme={theme}
                            onClick={(e) => { e.stopPropagation(); handleLoadSession(s.id, 'write'); }}
                            title="작성으로 열기"
                          >
                            <MessageSquare size={16} />
                          </ActionButton>
                          <ActionButton
                            theme={theme}
                            onClick={(e) => { e.stopPropagation(); handleLoadSession(s.id, 'edit'); }}
                            title="수정으로 열기"
                          >
                            <Edit3 size={16} />
                          </ActionButton>
                          <ActionButton
                            theme={theme}
                            onClick={(e) => { e.stopPropagation(); handleLoadSession(s.id, 'question'); }}
                            title="질문으로 열기"
                          >
                            <MessageCircle size={16} />
                          </ActionButton>
                        </FileActions>
                      </FileItem>
                    ))}
                  </FileList>
                )}
              </div>
            )}
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
}

export default RecentFiles;